// 账号管理：建账号（右上主操作展开表单）、列定义驱动的账号表（改角色 / 显示名 / WhatsApp、停用、重置密码）
import { useState } from "react";
import type { Role, UserDTO } from "@csp/shared";
import { api } from "../../lib/api";
import { useAlerts } from "../../lib/alerts";
import { ROLE_TEXT } from "../../lib/format";
import { useAction, useLoad } from "../../lib/hooks";
import { Badge } from "../../components/Badges";
import { ColumnPicker, DataTable, useHiddenColumns, type Column } from "../../components/DataTable";
import { useDialog } from "../../components/Dialogs";
import { SubTabs, SYSTEM_TABS } from "../../components/SubTabs";
import { PageHeading } from "../../components/PageHeading";

const ROLES: Role[] = ["clerk", "boss", "admin"];
const PHONE_RE = /^\+\d{8,15}$/;

export function UsersPage() {
  const list = useLoad(() => api.users(), []);
  const act = useAction();
  const dialog = useDialog();
  const { toast } = useAlerts();
  const [showForm, setShowForm] = useState(false);
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-users");
  const [form, setForm] = useState({ username: "", displayName: "", role: "clerk" as Role, password: "", whatsapp: "" });

  const create = async () => {
    if (form.whatsapp && !PHONE_RE.test(form.whatsapp)) return act.setError("WhatsApp 号码格式：+6591234567");
    if (form.password.length < 8) return act.setError("密码至少 8 位");
    const r = await act.run("create", () => api.createUser({ ...form, whatsapp: form.whatsapp || null }));
    if (r) {
      toast("ok", `已创建 ${r.username}`);
      setForm({ username: "", displayName: "", role: "clerk", password: "", whatsapp: "" });
      setShowForm(false);
      void list.reload();
    }
  };
  const update = async (u: UserDTO, patch: Parameters<typeof api.updateUser>[1]) => {
    const r = await act.run(u.id, () => api.updateUser(u.id, patch));
    if (r) void list.reload();
  };
  const editWa = async (u: UserDTO) => {
    const v = await dialog.prompt({ title: `${u.displayName} 的 WhatsApp`, label: "号码（留空 = 不推送）", placeholder: "+6591234567", initial: u.whatsapp ?? "" });
    if (v === null) return;
    if (v && !PHONE_RE.test(v)) return act.setError("WhatsApp 号码格式：+6591234567");
    await update(u, { whatsapp: v || null });
  };
  const editName = async (u: UserDTO) => {
    const v = await dialog.prompt({ title: "改显示名", label: "显示名", initial: u.displayName, required: true });
    if (v) await update(u, { displayName: v });
  };
  const resetPw = async (u: UserDTO) => {
    const v = await dialog.prompt({ title: `重置 ${u.username} 的密码`, body: <p>重置后该账号所有登录会失效。</p>, label: "新密码（至少 8 位）", required: true, okText: "重置" });
    if (!v) return;
    if (v.length < 8) return act.setError("密码至少 8 位");
    await update(u, { password: v });
    toast("ok", "密码已重置");
  };
  const toggleActive = async (u: UserDTO) => {
    if (u.active && !(await dialog.confirm({ title: `停用 ${u.username}？`, body: <p>停用后该账号马上不能登录。数据保留，可以随时恢复。</p>, okText: "停用账号", danger: true }))) return;
    await update(u, { active: !u.active });
  };

  const columns: Column<UserDTO>[] = [
    { key: "username", label: "用户名", fixed: true, render: (u) => <span className="mono">{u.username}</span> },
    {
      key: "name",
      label: "显示名",
      fixed: true,
      render: (u) => (
        <button className="btn btn-link" title="点击修改显示名" onClick={() => void editName(u)}>
          {u.displayName}
        </button>
      ),
    },
    {
      key: "role",
      label: "角色",
      render: (u) => (
        <select value={u.role} style={{ width: "auto" }} disabled={!!act.busy} onChange={(e) => void update(u, { role: e.target.value })} aria-label="角色">
          {ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_TEXT[r]}</option>
          ))}
        </select>
      ),
    },
    {
      key: "wa",
      label: "WhatsApp",
      render: (u) => (
        <button className="btn btn-link" title="点击修改 WhatsApp 号码" onClick={() => void editWa(u)}>
          {u.whatsapp ?? "（未设置）"}
        </button>
      ),
    },
    {
      key: "active",
      label: "状态",
      fixed: true,
      render: (u) => (u.active ? <Badge tone="green">正常</Badge> : <Badge tone="gray">已停用</Badge>),
    },
    {
      key: "op",
      label: "操作",
      fixed: true,
      render: (u) => (
        <div className="row">
          <button className="btn btn-small" disabled={!!act.busy} onClick={() => void resetPw(u)}>
            重置密码
          </button>
          <button className={`btn btn-small ${u.active ? "btn-danger" : ""}`} disabled={!!act.busy} onClick={() => void toggleActive(u)}>
            {u.active ? "停用账号" : "恢复账号"}
          </button>
        </div>
      ),
    },
  ];
  const rows = list.data ?? [];
  const inactive = rows.filter((u) => !u.active).length;

  return (
    <div>
      <PageHeading title="账号管理" desc="文员 / 老板 / 管理员账号。停用不删除数据，只是马上不能登录，可以随时恢复">
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)} aria-expanded={showForm}>
          {showForm ? "收起新建账号" : "＋ 新建账号"}
        </button>
      </PageHeading>
      <SubTabs tabs={SYSTEM_TABS} />
      {act.error && <div className="notice err">{act.error}</div>}
      {showForm && (
        <div className="card" data-testid="new-user-form">
          <h2>新建账号</h2>
          <div className="form-grid">
            <label className="field">
              <span className="lbl">
                用户名<span className="req">*</span>
              </span>
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="字母数字 . _ -" />
            </label>
            <label className="field">
              <span className="lbl">
                显示名<span className="req">*</span>
              </span>
              <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </label>
            <label className="field">
              <span className="lbl">角色</span>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_TEXT[r]}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="lbl">
                初始密码<span className="req">*</span>
              </span>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
              <span className="hint">至少 8 位</span>
            </label>
            <label className="field">
              <span className="lbl">WhatsApp（可空）</span>
              <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+6591234567" />
              <span className="hint">填了才会收到 WhatsApp 提醒</span>
            </label>
          </div>
          <div className="row end">
            <button className="btn btn-primary" disabled={!!act.busy || !form.username || !form.displayName} onClick={() => void create()}>
              {act.busy === "create" ? "正在创建…" : "创建账号"}
            </button>
          </div>
        </div>
      )}
      <div className="card">
        <div className="list-bar">
          <span className="count">
            共 {rows.length} 个账号{inactive > 0 && ` · 其中 ${inactive} 个已停用（整行变灰）`} · 显示名和 WhatsApp 点一下就能改
          </span>
          <ColumnPicker columns={columns} hidden={hidden} onToggle={toggleCol} />
        </div>
        {list.error && <div className="notice err">{list.error}</div>}
        {list.loading && !list.data ? (
          <div className="empty">正在加载…</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(u) => u.id}
            hidden={hidden}
            rowClass={(u) => (u.active ? undefined : "void")}
            rowAttrs={(u) => ({ "data-testid": "user-row", "data-username": u.username })}
            empty="还没有账号"
          />
        )}
      </div>
    </div>
  );
}
